import { useEffect, useState } from "react";
import { Archive, Pencil, Plus, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RISK_STYLE = {
  "LÅG": "bg-emerald-100 text-emerald-800",
  "MEDEL": "bg-amber-100 text-amber-800",
  "HÖG": "bg-red-100 text-red-800",
};
const EMPTY_FORM = { name: "", org_nr: "", industry: "", contact_person: "", contact_email: "", risk_level: "MEDEL", risk_notes: "" };

export default function ClientsTab() {
  const { user } = useAuth();
  const isManager = ["Kvalitetsansvarig", "Byråledning", "Supervisor"].includes(user.role);
  const [clients, setClients] = useState(null);
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null); // {mode: 'create'|'edit'|'engagement', client}
  const [form, setForm] = useState(EMPTY_FORM);
  const [engForm, setEngForm] = useState({ engagement_type: "", deadline: "", assigned_consultant: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/clients").then((r) => setClients(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY_FORM); setModal({ mode: "create" }); };
  const openEdit = (c) => {
    setForm({ name: c.name, org_nr: c.org_nr, industry: c.industry || "", contact_person: c.contact_person || "",
              contact_email: c.contact_email || "", risk_level: c.risk_level || "MEDEL", risk_notes: c.risk_notes || "" });
    setModal({ mode: "edit", client: c });
  };
  const openEngagement = (c) => { setEngForm({ engagement_type: "", deadline: "", assigned_consultant: "" }); setModal({ mode: "engagement", client: c }); };

  const saveClient = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal.mode === "create") {
        await api.post("/clients", form);
        toast.success(`Kunden ${form.name} har skapats`);
      } else {
        await api.put(`/clients/${modal.client.id}`, form);
        toast.success("Kunden har uppdaterats");
      }
      setModal(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const archive = async (c) => {
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success(`Kunden ${c.name} har arkiverats`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveEngagement = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/engagements", { client_id: modal.client.id, ...engForm });
      toast.success(`Uppdraget "${engForm.engagement_type}" har tilldelats med Reko-checklista`);
      setModal(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  if (!clients) return <div className="text-sm text-slate-500">Laddar...</div>;
  const consultants = users.filter((u) => ["Konsult", "Kvalitetsansvarig", "Byråledning"].includes(u.role));

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="clients-view">
      <div className="flex justify-between items-center border-b border-slate-200 pb-4 mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-slate-900">Kund- & Uppdragshantering</h2>
          <p className="text-sm text-slate-500 mt-1">Lägg till nya kunder, sätt riskprofiler och tilldela Reko-checklistor.</p>
        </div>
        {isManager && (
          <button data-testid="client-add-btn" onClick={openCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4 py-2 rounded shadow flex items-center">
            <UserPlus className="w-3.5 h-3.5 mr-1.5" />Ny Kund
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs border-collapse" data-testid="clients-table">
          <thead>
            <tr className="bg-slate-100 text-slate-600 font-bold border-b border-slate-200">
              <th className="p-3">Kundnamn</th><th className="p-3">Org.nr</th><th className="p-3">Bransch</th>
              <th className="p-3">Riskprofil</th><th className="p-3">Tilldelade Uppdrag</th><th className="p-3 text-right">Åtgärd</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {clients.map((c) => (
              <tr key={c.id}>
                <td className="p-3 font-bold text-slate-800">{c.name}</td>
                <td className="p-3 font-mono">{c.org_nr}</td>
                <td className="p-3">{c.industry || "—"}</td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded font-bold ${RISK_STYLE[c.risk_level || "MEDEL"]}`}>{c.risk_level || "MEDEL"}</span></td>
                <td className="p-3 text-slate-600">{c.engagements?.length ? c.engagements.join(", ") : "—"}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  {isManager ? (
                    <>
                      <button data-testid={`client-edit-${c.id}`} onClick={() => openEdit(c)} className="bg-slate-100 hover:bg-slate-200 px-2.5 py-1 rounded mr-1 font-semibold">
                        <Pencil className="w-3 h-3 inline mr-1" />Redigera
                      </button>
                      <button data-testid={`client-engagement-${c.id}`} onClick={() => openEngagement(c)} className="bg-blue-50 text-blue-600 hover:bg-blue-100 px-2.5 py-1 rounded font-semibold mr-1">
                        <Plus className="w-3 h-3 inline" /> Uppdrag
                      </button>
                      <button data-testid={`client-archive-${c.id}`} onClick={() => archive(c)} className="bg-red-50 text-red-600 hover:bg-red-100 px-2.5 py-1 rounded font-semibold">
                        <Archive className="w-3 h-3 inline" />
                      </button>
                    </>
                  ) : <span className="text-slate-400">—</span>}
                </td>
              </tr>
            ))}
            {clients.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">Inga kunder registrerade</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && (modal.mode === "create" || modal.mode === "edit") && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="client-modal">
          <form onSubmit={saveClient} className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-3 border border-slate-200">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">{modal.mode === "create" ? "Ny Kund" : `Redigera ${modal.client.name}`}</h3>
              <button type="button" data-testid="client-modal-close" onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="col-span-2">
                <label className="block font-semibold text-slate-700 mb-1">* Kundnamn</label>
                <input data-testid="client-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full p-2 border border-slate-300 rounded" />
              </div>
              <div>
                <label className="block font-semibold text-slate-700 mb-1">* Org.nr</label>
                <input data-testid="client-orgnr-input" required value={form.org_nr} onChange={(e) => setForm({ ...form, org_nr: e.target.value })} className="w-full p-2 border border-slate-300 rounded" placeholder="556123-4567" />
              </div>
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Bransch</label>
                <input data-testid="client-industry-input" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="w-full p-2 border border-slate-300 rounded" />
              </div>
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Kontaktperson</label>
                <input data-testid="client-contact-input" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="w-full p-2 border border-slate-300 rounded" />
              </div>
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Kontakt-e-post</label>
                <input data-testid="client-email-input" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="w-full p-2 border border-slate-300 rounded" />
              </div>
              <div>
                <label className="block font-semibold text-slate-700 mb-1">* Riskprofil</label>
                <select data-testid="client-risk-select" value={form.risk_level} onChange={(e) => setForm({ ...form, risk_level: e.target.value })} className="w-full p-2 border border-slate-300 rounded">
                  <option value="LÅG">LÅG</option><option value="MEDEL">MEDEL</option><option value="HÖG">HÖG</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block font-semibold text-slate-700 mb-1">Riskanteckningar</label>
                <textarea data-testid="client-risknotes-input" value={form.risk_notes} onChange={(e) => setForm({ ...form, risk_notes: e.target.value })} className="w-full p-2 border border-slate-300 rounded h-16" placeholder="Kundens unika risker..." />
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100">
              <button type="button" onClick={() => setModal(null)} className="px-4 py-2 bg-slate-100 text-slate-700 text-xs font-semibold rounded hover:bg-slate-200">Avbryt</button>
              <button data-testid="client-save-btn" type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded hover:bg-blue-700 shadow disabled:opacity-60">
                {saving ? "Sparar..." : "Spara kund"}
              </button>
            </div>
          </form>
        </div>
      )}

      {modal && modal.mode === "engagement" && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="engagement-modal">
          <form onSubmit={saveEngagement} className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-3 border border-slate-200">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Nytt uppdrag — {modal.client.name}</h3>
              <button type="button" onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-slate-500">En Reko-checklista genereras automatiskt baserat på kundens riskprofil ({modal.client.risk_level || "MEDEL"}).</p>
            <div className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">* Uppdragstyp</label>
                <input data-testid="engagement-type-input" required value={engForm.engagement_type} onChange={(e) => setEngForm({ ...engForm, engagement_type: e.target.value })} className="w-full p-2 border border-slate-300 rounded" placeholder="t.ex. Årsredovisning 2026" />
              </div>
              <div>
                <label className="block font-semibold text-slate-700 mb-1">* Deadline</label>
                <input data-testid="engagement-deadline-input" type="date" required value={engForm.deadline} onChange={(e) => setEngForm({ ...engForm, deadline: e.target.value })} className="w-full p-2 border border-slate-300 rounded" />
              </div>
              <div>
                <label className="block font-semibold text-slate-700 mb-1">* Ansvarig konsult</label>
                <select data-testid="engagement-consultant-select" required value={engForm.assigned_consultant} onChange={(e) => setEngForm({ ...engForm, assigned_consultant: e.target.value })} className="w-full p-2 border border-slate-300 rounded">
                  <option value="">— Välj konsult —</option>
                  {consultants.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100">
              <button type="button" onClick={() => setModal(null)} className="px-4 py-2 bg-slate-100 text-slate-700 text-xs font-semibold rounded hover:bg-slate-200">Avbryt</button>
              <button data-testid="engagement-save-btn" type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded hover:bg-blue-700 shadow disabled:opacity-60">
                {saving ? "Skapar..." : "Skapa uppdrag"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
